import React, { useEffect, useRef } from "react";
import Reveal from "./Reveal";
import Doodle from "./Doodle";
import Highlighter from "./Highlighter";

// Act 2 of the story site: the mesh.
//
// This section exists on neither reference site and is the one original idea in
// the revamp. "People you mesh with" is currently just a phrase; here it becomes
// the visual. Scattered dots (people, alone) draw connections to each other as
// you scroll, and the completed mesh resolves into photographs of people
// already together.
//
// Canvas rather than CSS or SVG, because the connections are generated from
// node distance and scroll progress rather than authored by hand.
//
// Accessibility and robustness:
// - the node layout is SEEDED, not random, so the mesh looks identical on every
//   load and there is no hydration mismatch;
// - under prefers-reduced-motion the mesh renders complete and static, since the
//   assembly is decorative and the end state carries the meaning;
// - the canvas is aria-hidden and the section's meaning lives in the real text.

const NODES = 34;
const LINK_DISTANCE = 0.27; // as a fraction of the canvas diagonal

/** Deterministic PRNG (mulberry32) so the mesh is stable across renders. */
function seeded(seed: number) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const MeshMoment: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const progressRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const section = sectionRef.current;
    if (!canvas || !section) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // Seeded node field, in normalised 0..1 space so it survives resizes.
    const rand = seeded(20260731);
    const nodes = Array.from({ length: NODES }, () => ({
      x: rand(),
      y: rand(),
      r: 2 + rand() * 3.2,
    }));

    let width = 0;
    let height = 0;
    let frame = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const readAccent = () => {
      const styles = getComputedStyle(document.documentElement);
      return {
        dot: styles.getPropertyValue("--color-salmon").trim() || "#ffa07a",
        ink: styles.getPropertyValue("--color-ink").trim() || "#2d3849",
      };
    };

    const draw = () => {
      const p = reduced ? 1 : progressRef.current;
      const { dot, ink } = readAccent();
      ctx.clearRect(0, 0, width, height);

      const diag = Math.hypot(width, height);
      const maxLink = diag * LINK_DISTANCE;

      // Connections first, so nodes sit on top of their own lines.
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const ax = nodes[i].x * width;
          const ay = nodes[i].y * height;
          const bx = nodes[j].x * width;
          const by = nodes[j].y * height;
          const d = Math.hypot(bx - ax, by - ay);
          if (d > maxLink) continue;

          // Near links connect early, far links connect last, so the mesh
          // grows outward from clusters rather than appearing all at once.
          const threshold = d / maxLink;
          const strength = (p - threshold * 0.85) / 0.15;
          const alpha = Math.max(0, Math.min(1, strength)) * (1 - d / maxLink);
          if (alpha <= 0.01) continue;

          ctx.strokeStyle = ink;
          ctx.globalAlpha = alpha * 0.28;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.lineTo(bx, by);
          ctx.stroke();
        }
      }

      for (const n of nodes) {
        ctx.globalAlpha = 0.35 + p * 0.65;
        ctx.fillStyle = dot;
        ctx.beginPath();
        ctx.arc(n.x * width, n.y * height, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the section enters from below, 1 once it is well into view.
      const raw = (vh - rect.top) / (vh * 0.85);
      const p = Math.max(0, Math.min(1, raw));
      progressRef.current = p;
      frame = requestAnimationFrame(draw);
    };

    resize();
    onScroll();
    draw();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", () => {
      resize();
      draw();
    });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="mesh"
      className="relative w-full overflow-hidden bg-cream py-28 md:py-36 px-6"
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute inset-0 w-full h-full"
      />

      <div className="relative max-w-6xl mx-auto">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-salmon-dark mb-4">
            Why it works
          </p>
          <Reveal y={20}>
            <h2
              className="font-headline text-ink tracking-[-0.025em] leading-[1.16] text-[clamp(2.1rem,6.2vw,4rem)] text-balance"
              style={{ fontWeight: 700 }}
            >
              A city is not short of people. It is short of{" "}
              <Highlighter tone="gold">reasons to meet them.</Highlighter>
            </h2>
          </Reveal>
          <Reveal y={14} delay={120}>
            <p className="mt-6 text-lg md:text-xl text-ink/70 leading-relaxed">
              Everyone around you is one plan away from being someone you know.
              LetsLoop is the plan.
            </p>
          </Reveal>
        </div>

        {/* Sticker cut-outs, overlapped and tilted, rather than two tidy
            rectangles. Rotation is right HERE and wrong on the step cards: a
            sticker is meant to look stuck on, a content card is not. Cut with
            the same rembg pass the posters use (LL-0189). */}
        <div className="relative mt-14 md:mt-20 max-w-4xl mx-auto">
          <Reveal y={30} className="relative z-10 md:w-[58%]">
            <img
              src="/story/gather-sticker.webp"
              srcSet="/story/gather-sticker-sm.webp 420w, /story/gather-sticker.webp 760w"
              sizes="(max-width: 768px) 88vw, 44vw"
              alt="A group of friends laughing together in a living room"
              loading="lazy"
              className="w-full h-auto -rotate-2 drop-shadow-[0_26px_40px_rgba(45,56,73,0.18)]"
            />
          </Reveal>
          {/* A FRAMED photo, and deliberately a different image from the one
              closing the page. `embrace` was appearing twice; it now earns its
              moment on the coral section alone. Framed rather than cut out
              because this is a table scene, and a cut-out of people around a
              table floats without the table that explains them. The mix also
              gives the section two textures instead of two of the same. */}
          <Reveal
            y={34}
            delay={140}
            className="relative z-20 -mt-12 ml-auto md:mt-0 md:absolute md:right-0 md:bottom-[-4%] md:w-[54%]"
          >
            <div className="rotate-2 polaroid-frame p-2.5 pb-7 shadow-[0_26px_44px_-24px_rgba(45,56,73,0.45)]">
              <img
                src="/story/kitchen.webp"
                srcSet="/story/kitchen-sm.webp 600w, /story/kitchen.webp 1200w"
                sizes="(max-width: 768px) 84vw, 42vw"
                alt="Four friends laughing around a kitchen table over coffee"
                loading="lazy"
                className="w-full h-auto"
              />
            </div>
          </Reveal>
          <Doodle
            name="burst"
            size={40}
            className="hidden md:block absolute left-[46%] top-[6%] text-salmon z-30"
          />
          <Doodle
            name="arrow"
            size={64}
            rotate={-18}
            className="absolute w-8 h-8 md:w-auto md:h-auto left-[-4%] max-md:left-[5%] bottom-[8%] text-teal/50 z-30"
          />
          <Doodle
            name="starFilled"
            size={26}
            className="absolute w-8 h-8 md:w-auto md:h-auto right-[6%] top-[-4%] text-salmon/70 z-30"
          />
        </div>
      </div>
    </section>
  );
};

export default MeshMoment;
